/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { jsx } from '@compiled/react';
import { type Extension } from '@atlassian/forge-ui/ui';
import FeatureGates from '@atlaskit/feature-gate-js-client';

import { type CoreData, type ExtensionData } from '@atlassian/forge-ui-types';
import { useInvokeAdfExportExtension } from '../../utils/useInvokeAdfExportExtension';
import { ReactRenderer } from '@atlaskit/renderer';
import { type ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import { RendererActionsContext } from '@atlaskit/renderer/actions';
import { Loader } from '@atlassian/forge-ui/ui';
import { useIntl } from 'react-intl';
import SectionMessage from '@atlaskit/section-message';
import { messages } from '../i18n';

interface AdfExportRendererProps {
	accountId: string;
	contextIds: string[];
	coreData: CoreData;
	dataProviders: ProviderFactory;
	extension: Extension;
	extensionData: ExtensionData;
	extensionId: string;
	extensionPayload: Record<string, any>;
	extensionTitle?: string;
	fallbackRenderer?: () => JSX.Element;
	getContextToken?: () => Promise<string>;
}

export const AdfExportRenderer = ({
	accountId,
	contextIds,
	coreData,
	dataProviders,
	extensionData,
	extensionId,
	extensionPayload,
	fallbackRenderer,
	getContextToken,
}: AdfExportRendererProps): JSX.Element | null => {
	const { adfDoc, error, isLoading } = useInvokeAdfExportExtension({
		accountId,
		contextIds,
		extensionId,
		coreData,
		extensionData,
		extensionPayload,
		getContextToken,
	});
	const intl = useIntl();

	if (error) {
		return (
			<SectionMessage appearance="error">
				{intl.formatMessage(messages.exportMacroError)}
			</SectionMessage>
		);
	}

	if (isLoading) {
		return <Loader />;
	}

	// adfDoc is null/undefined - vendor intentionally returned null/undefined to signal
	// "don't use ADF rendering for this export type". Use fallback renderer if provided
	// and the experiment is enabled, otherwise render nothing.
	if (!adfDoc) {
		const fallbackEnabled = FeatureGates.getExperimentValue(
			'forge_allow_skipadfexportforpdf',
			'isEnabled',
			false,
		);
		return fallbackEnabled && fallbackRenderer ? fallbackRenderer() : null;
	}

	// Successfully got ADF doc - render it
	return (
		<RendererActionsContext>
			<ReactRenderer dataProviders={dataProviders} document={adfDoc} />
		</RendererActionsContext>
	);
};
